import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, FlatList, Dimensions, StyleSheet, Platform } from 'react-native';
import { Card, Title, Paragraph, Chip, Text } from 'react-native-paper';

const { width: screenWidth } = Dimensions.get('window');
const CARD_WIDTH = Platform.OS === 'web' 
  ? Math.min(screenWidth - 128, 420)
  : Math.min(screenWidth - 64, 360);
const CARD_SPACING = 16;
const SNAP_INTERVAL = CARD_WIDTH + CARD_SPACING;

const CARDS = [
  { key: 'pisti', type: 'pisti' },
  { key: 'uno', type: 'uno' },
  { key: 'batak', type: 'batak' },
];

function GameCarousel({
  stateRef,
  pistiState,
  unoState,
  batakState,
  onOpenPisti,
  onOpenUno,
  onOpenBatak,
}) {
  const listRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(() => stateRef?.current?.activeIndex || 0);
  const isInteractingRef = useRef(false);
  const autoScrollIntervalRef = useRef(null);
  const isAutoScrollingRef = useRef(false);

  useEffect(() => {
    if (stateRef) {
      stateRef.current = stateRef.current || {};
      stateRef.current.activeIndex = activeIndex;
    }
  }, [activeIndex, stateRef]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      listRef.current?.scrollToOffset({
        offset: activeIndex * SNAP_INTERVAL,
        animated: false,
      });
    }, 0);
    return () => clearTimeout(timeout);
  }, []);

  // Auto-scroll: 3 saniyede bir sağa sola kaydır
  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    const startAutoScroll = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }

      autoScrollIntervalRef.current = setInterval(() => {
        // Kullanıcı etkileşimde değilse ve auto-scroll devam ediyorsa
        if (!isInteractingRef.current && !isAutoScrollingRef.current) {
          isAutoScrollingRef.current = true;
          
          // Mevcut index'i al ve sıradaki index'e geç (0->1->2->0 döngüsü)
          setActiveIndex((currentIdx) => {
            const nextIdx = (currentIdx + 1) % CARDS.length;
            
            // Scroll işlemini başlat
            setTimeout(() => {
              listRef.current?.scrollToOffset({
                offset: nextIdx * SNAP_INTERVAL,
                animated: true,
              });
              
              // Auto-scroll tamamlandıktan sonra flag'i temizle
              setTimeout(() => {
                isAutoScrollingRef.current = false;
              }, 500);
            }, 0);
            
            return nextIdx;
          });
        }
      }, 3000);
    };

    // İlk yüklemede 3 saniye bekle, sonra başlat
    const initialTimeout = setTimeout(() => {
      startAutoScroll();
    }, 3000);

    return () => {
      clearTimeout(initialTimeout);
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, []);

  const snapOffsets = useMemo(
    () => CARDS.map((_, index) => index * SNAP_INTERVAL),
    []
  );

  const updateActiveIndex = useCallback((offsetX) => {
    const nextIndex = Math.round(offsetX / SNAP_INTERVAL);
    const clamped = Math.max(0, Math.min(nextIndex, CARDS.length - 1));
    setActiveIndex((prev) => (prev === clamped ? prev : clamped));
  }, []);

  const handleScrollBegin = useCallback(() => {
    isInteractingRef.current = true;
    // Auto-scroll'u durdur
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, []);

  const handleScrollEnd = useCallback((event) => {
    const velocityX = event.nativeEvent?.velocity?.x ?? 0;
    if (Math.abs(velocityX) < 0.1) {
      updateActiveIndex(event.nativeEvent.contentOffset.x);
      isInteractingRef.current = false;
      
      // Web'de auto-scroll'u yeniden başlat (3 saniye sonra)
      if (Platform.OS === 'web') {
        setTimeout(() => {
          if (!isInteractingRef.current && autoScrollIntervalRef.current === null) {
            autoScrollIntervalRef.current = setInterval(() => {
              if (!isInteractingRef.current && !isAutoScrollingRef.current) {
                isAutoScrollingRef.current = true;
                setActiveIndex((currentIdx) => {
                  const nextIdx = currentIdx === 0 ? 1 : 0;
                  setTimeout(() => {
                    listRef.current?.scrollToOffset({
                      offset: nextIdx * SNAP_INTERVAL,
                      animated: true,
                    });
                    setTimeout(() => {
                      isAutoScrollingRef.current = false;
                    }, 500);
                  }, 0);
                  return nextIdx;
                });
              }
            }, 3000);
          }
        }, 3000);
      }
    }
  }, [updateActiveIndex]);

  const handleMomentumEnd = useCallback((event) => {
    updateActiveIndex(event.nativeEvent.contentOffset.x);
    isInteractingRef.current = false;
    
    // Web'de auto-scroll'u yeniden başlat (3 saniye sonra)
    if (Platform.OS === 'web') {
      setTimeout(() => {
        if (!isInteractingRef.current && autoScrollIntervalRef.current === null) {
          autoScrollIntervalRef.current = setInterval(() => {
            if (!isInteractingRef.current && !isAutoScrollingRef.current) {
              isAutoScrollingRef.current = true;
              setActiveIndex((currentIdx) => {
                const nextIdx = currentIdx === 0 ? 1 : 0;
                setTimeout(() => {
                  listRef.current?.scrollToOffset({
                    offset: nextIdx * SNAP_INTERVAL,
                    animated: true,
                  });
                  setTimeout(() => {
                    isAutoScrollingRef.current = false;
                  }, 500);
                }, 0);
                return nextIdx;
              });
            }
          }, 3000);
        }
      }, 3000);
    }
  }, [updateActiveIndex]);

  const renderPistiCard = useCallback(() => {
    const activeRooms = pistiState?.activeRooms || 0;
    const totalPlayers = pistiState?.totalPlayers || 0;

    return (
      <Card 
        style={[styles.card, styles.gameCard]} 
        onPress={(e) => {
          // Web'de sayfa yenilenmesini önle (sadece stopPropagation)
          if (e && e.stopPropagation) {
            e.stopPropagation();
          }
          onOpenPisti(e);
        }}
      >
        <Card.Content>
          <View style={styles.gameCardHeader}>
            <View style={{ flex: 1 }}>
              <Title style={styles.gameCardTitle}>Pişti</Title>
            </View>
            <Chip style={styles.gameCardBadge} textStyle={styles.gameCardBadgeText}>
              Oyun
            </Chip>
          </View>
          <Paragraph style={styles.gameCardDescription}>
            Klasik kart oyunu - 2-4 oyuncu
          </Paragraph>
          <View style={styles.gameCardFooter}>
            <View style={styles.gameCardStat}>
              <Text style={styles.gameCardStatLabel}>Aktif Oda</Text>
              <Text style={styles.gameCardStatValue}>{activeRooms}</Text>
            </View>
            <View style={[styles.gameCardStat, { flex: 2, paddingLeft: 12 }]}>
              <Text style={styles.gameCardStatLabel}>Oyuncu</Text>
              <Text style={styles.gameCardStatValue}>
                {totalPlayers}
              </Text>
            </View>
            <View style={styles.gameCardButtonRow}>
              <Chip
                style={[styles.gameCardActionChip, styles.gameCardActionChipGreen]}
                textStyle={styles.gameCardActionChipText}
                onPress={(e) => {
                  // Web'de sayfa yenilenmesini önle (sadece stopPropagation)
                  if (e && e.stopPropagation) {
                    e.stopPropagation();
                  }
                  onOpenPisti(e);
                }}
              >
                Katıl
              </Chip>
            </View>
          </View>
        </Card.Content>
      </Card>
    );
  }, [pistiState, onOpenPisti]);

  const renderUnoCard = useCallback(() => {
    const activeRooms = unoState?.activeRooms || 0;
    const totalPlayers = unoState?.totalPlayers || 0;

    return (
      <Card 
        style={[styles.card, styles.gameCard]} 
        onPress={(e) => {
          if (e && e.stopPropagation) {
            e.stopPropagation();
          }
          onOpenUno?.(e);
        }}
      >
        <Card.Content>
          <View style={styles.gameCardHeader}>
            <View style={{ flex: 1 }}>
              <Title style={styles.gameCardTitle}>UNO</Title>
            </View>
            <Chip style={styles.gameCardBadge} textStyle={styles.gameCardBadgeText}>
              Oyun
            </Chip>
          </View>
          <Paragraph style={styles.gameCardDescription}>
            Renkleri eşleştir, özel kartlarla rakiplerini yavaşlat!
          </Paragraph>
          <View style={styles.gameCardFooter}>
            <View style={styles.gameCardStat}>
              <Text style={styles.gameCardStatLabel}>Aktif Oda</Text>
              <Text style={styles.gameCardStatValue}>{activeRooms}</Text>
            </View>
            <View style={[styles.gameCardStat, { flex: 2, paddingLeft: 12 }]}>
              <Text style={styles.gameCardStatLabel}>Oyuncu</Text>
              <Text style={styles.gameCardStatValue}>
                {totalPlayers}
              </Text>
            </View>
            <View style={styles.gameCardButtonRow}>
              <Chip
                style={[styles.gameCardActionChip, styles.gameCardActionChipGreen]}
                textStyle={styles.gameCardActionChipText}
                onPress={(e) => {
                  if (e && e.stopPropagation) {
                    e.stopPropagation();
                  }
                  onOpenUno?.(e);
                }}
              >
                Katıl
              </Chip>
            </View>
          </View>
        </Card.Content>
      </Card>
    );
  }, [unoState, onOpenUno]);

  const renderBatakCard = useCallback(() => {
    const activeRooms = batakState?.activeRooms || 0;
    const totalPlayers = batakState?.totalPlayers || 0;

    return (
      <Card
        style={[styles.card, styles.gameCard]}
        onPress={(e) => {
          if (e && e.stopPropagation) {
            e.stopPropagation();
          }
          onOpenBatak?.(e);
        }}
      >
        <Card.Content>
          <View style={styles.gameCardHeader}>
            <View style={{ flex: 1 }}>
              <Title style={styles.gameCardTitle}>Batak</Title>
            </View>
            <Chip style={styles.gameCardBadge} textStyle={styles.gameCardBadgeText}>
              Oyun
            </Chip>
          </View>
          <Paragraph style={styles.gameCardDescription}>
            Koz maça! 4 oyunculu ihale kart oyunu.
          </Paragraph>
          <View style={styles.gameCardFooter}>
            <View style={styles.gameCardStat}>
              <Text style={styles.gameCardStatLabel}>Aktif Oda</Text>
              <Text style={styles.gameCardStatValue}>{activeRooms}</Text>
            </View>
            <View style={[styles.gameCardStat, { flex: 2, paddingLeft: 12 }]}>
              <Text style={styles.gameCardStatLabel}>Oyuncu</Text>
              <Text style={styles.gameCardStatValue}>
                {totalPlayers}
              </Text>
            </View>
            <View style={styles.gameCardButtonRow}>
              <Chip
                style={[styles.gameCardActionChip, styles.gameCardActionChipGreen]}
                textStyle={styles.gameCardActionChipText}
                onPress={(e) => {
                  if (e && e.stopPropagation) {
                    e.stopPropagation();
                  }
                  onOpenBatak?.(e);
                }}
              >
                Katıl
              </Chip>
            </View>
          </View>
        </Card.Content>
      </Card>
    );
  }, [batakState, onOpenBatak]);

  const renderItem = useCallback(({ item }) => (
    <View style={[styles.gameCarouselItem, { width: CARD_WIDTH, marginRight: CARD_SPACING }]}>
      {item.type === 'pisti' ? renderPistiCard() :
       item.type === 'uno' ? renderUnoCard() :
       item.type === 'batak' ? renderBatakCard() : null}
    </View>
  ), [renderPistiCard, renderUnoCard, renderBatakCard]);

  return (
    <View style={styles.gameCarousel}>
      <FlatList
        ref={listRef}
        horizontal
        data={CARDS}
        keyExtractor={(item) => item.key}
        showsHorizontalScrollIndicator={false}
        snapToInterval={Platform.OS === 'web' ? undefined : SNAP_INTERVAL}
        snapToAlignment={Platform.OS === 'web' ? undefined : 'start'}
        snapToOffsets={Platform.OS === 'web' ? undefined : snapOffsets}
        decelerationRate="fast"
        bounces={Platform.OS === 'web' ? true : false}
        disableIntervalMomentum={Platform.OS === 'web' ? false : true}
        scrollEnabled={true}
        contentContainerStyle={styles.gameCarouselList}
        onScrollBeginDrag={handleScrollBegin}
        onScrollEndDrag={handleScrollEnd}
        onMomentumScrollEnd={handleMomentumEnd}
        getItemLayout={(data, index) => ({
          length: SNAP_INTERVAL,
          offset: SNAP_INTERVAL * index,
          index,
        })}
        onScrollBeginDrag={handleScrollBegin}
        onScrollEndDrag={handleScrollEnd}
        onMomentumScrollEnd={handleMomentumEnd}
        renderItem={renderItem}
      />
      <View style={styles.gameCarouselDots}>
        {CARDS.map((card, index) => (
          <View
            key={card.key}
            style={[
              styles.gameCarouselDot,
              index === activeIndex && styles.gameCarouselDotActive,
            ]}
          />
        ))}
      </View>
    </View>
  );
}

export default React.memo(GameCarousel);

const styles = StyleSheet.create({
  gameCarousel: {
    paddingVertical: Platform.OS === 'web' ? 20 : 12,
  },
  gameCarouselList: {
    paddingHorizontal: Platform.OS === 'web' ? 32 : 16,
  },
  gameCarouselItem: {
    borderRadius: 16,
  },
  gameCarouselDots: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 8,
    gap: 6,
  },
  gameCarouselDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#333741',
  },
  gameCarouselDotActive: {
    backgroundColor: '#FF4444',
    width: 16,
  },
  card: {
    backgroundColor: '#151A22',
    borderRadius: Platform.OS === 'web' ? 24 : 18,
    padding: Platform.OS === 'web' ? 6 : 4,
    ...(Platform.OS === 'web' ? {
      borderWidth: 1,
      borderColor: '#2A2A2A',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
    } : {}),
  },
  gameCard: {
    backgroundColor: '#151A22',
  },
  gameCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  gameCardTitle: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: Platform.OS === 'web' ? 24 : 20,
    ...(Platform.OS === 'web' ? {
      letterSpacing: -0.5,
    } : {}),
  },
  gameCardDescription: {
    color: '#B0B7C3',
    fontSize: Platform.OS === 'web' ? 16 : 14,
    marginBottom: Platform.OS === 'web' ? 20 : 16,
    ...(Platform.OS === 'web' ? {
      lineHeight: 22,
    } : {}),
  },
  gameCardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  gameCardStat: {
    flex: 1,
  },
  gameCardStatLabel: {
    color: '#8C92A4',
    fontSize: 12,
    marginBottom: 4,
  },
  gameCardStatValue: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  gameCardBadge: {
    backgroundColor: '#333F5B',
    borderRadius: 12,
  },
  gameCardBadgeText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 12,
  },
  gameCardButtonRow: {
    alignItems: 'flex-end',
  },
  gameCardActionChip: {
    backgroundColor: '#FF4444',
  },
  gameCardActionChipGreen: {
    backgroundColor: '#4CAF50',
  },
  gameCardActionChipText: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
  gameCardLeaderboard: {
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#2C3240',
  },
  gameCardLeaderboardItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  gameCardLeaderboardName: {
    color: '#E6EAF2',
    fontSize: 13,
    flex: 1,
    marginRight: 8,
  },
  gameCardLeaderboardScore: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  gameCardUsedWords: {
    marginTop: 12,
  },
  gameCardUsedWordsText: {
    color: '#C7D0E0',
    fontSize: 13,
  },
});

